import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, FileText, Search, Plus, X, Mic } from "lucide-react";
import { listProposals, createBlankProposal, createProposalFromDictation } from "@/lib/proposals.functions";
import { DictationDialog, type DictationFields } from "@/components/dictation/DictationDialog";

export const Route = createFileRoute("/_authenticated/admin/proposals/")({
  component: ProposalsList,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

const STATUSES = ["draft","sent","viewed","approved","declined","changes_requested","expired","void"] as const;
const STATUS_COLOR: Record<string, string> = {
  draft: "bg-zinc-500/20 text-zinc-900 dark:text-zinc-200 border-zinc-500/40",
  sent: "bg-blue-500/20 text-blue-900 dark:text-blue-200 border-blue-500/40",
  viewed: "bg-amber-300/20 text-amber-900 dark:text-amber-200 border-amber-300/40",
  approved: "bg-emerald-500/20 text-emerald-900 dark:text-emerald-200 border-emerald-500/40",
  declined: "bg-red-500/20 text-red-900 dark:text-red-200 border-red-500/40",
  changes_requested: "bg-orange-500/20 text-orange-900 dark:text-orange-200 border-orange-500/40",
  expired: "bg-zinc-500/20 text-zinc-900 dark:text-zinc-300 border-zinc-500/40",
  void: "bg-zinc-700/25 text-zinc-900 dark:text-zinc-400 border-zinc-500/40",
};

function ProposalsList() {
  const fn = useServerFn(listProposals);
  const createFn = useServerFn(createBlankProposal);
  const createFromDictationFn = useServerFn(createProposalFromDictation);
  const navigate = useNavigate();
  const [statusFilter, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showDictate, setShowDictate] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "proposals", statusFilter, search],
    queryFn: () => fn({ data: { status: statusFilter || undefined, search: search || undefined, limit: 300 } }),
  });

  const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  return (
    <div className="space-y-6 max-w-7xl">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Proposals</p>
          <h1 className="font-serif text-3xl text-white mt-1">Customer proposals</h1>
          <p className="text-sm text-zinc-500 mt-1">Generate, send, and track customer proposals.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowDictate(true)} className="inline-flex items-center gap-1.5 text-xs bg-amber-300 text-zinc-950 font-semibold px-3 py-2 rounded-lg hover:bg-amber-200">
            <Mic className="size-3.5" /> Dictate proposal
          </button>
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 text-xs bg-white/10 text-white font-semibold px-3 py-2 rounded-lg hover:bg-white/15 border border-white/10">
            <Plus className="size-3.5" /> New blank proposal
          </button>
          <Link to="/admin/estimates" className="inline-flex items-center gap-1.5 text-xs bg-amber-300 text-zinc-950 font-semibold px-3 py-2 rounded-lg" title="Pick an estimate and click Generate proposal">
            <Plus className="size-3.5" /> Generate from estimate
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number, customer, address…"
            className="pl-9 pr-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 w-80"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {isLoading ? (
          <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-zinc-500 text-sm">
            <FileText className="size-8 mx-auto mb-2 opacity-40" />
            No proposals yet — open an estimate and click <span className="text-amber-300">Generate Proposal</span>.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Number</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Project</th>
                <th className="text-left px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Deposit</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Expires</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p: any) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-3">
                    <Link to="/admin/proposals/$id" params={{ id: p.id }} className="text-amber-300 font-mono tabular-nums">
                      {p.proposal_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-white">{p.customer_name}<div className="text-xs text-zinc-500">{p.customer_email}</div></td>
                  <td className="px-4 py-3 text-zinc-300 capitalize">{p.project_type || "—"}</td>
                  <td className="px-4 py-3 text-zinc-200 tabular-nums">{fmt(Number(p.total))}</td>
                  <td className="px-4 py-3 text-zinc-400 tabular-nums text-xs">{p.deposit_required ? fmt(Number(p.deposit_amount)) : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full border capitalize ${STATUS_COLOR[p.status] || "bg-zinc-500/20 text-zinc-900 dark:text-zinc-200 border-zinc-500/40"}`}>{p.status.replace("_", " ")}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNew && (
        <NewProposalDialog
          onClose={() => setShowNew(false)}
          onCreate={async (values) => {
            const res = await createFn({ data: values });
            setShowNew(false);
            navigate({ to: "/admin/proposals/$id", params: { id: res.id } });
          }}
        />
      )}
      {showDictate && (
        <DictationDialog
          mode="proposal"
          onClose={() => setShowDictate(false)}
          applyLabel="Create proposal"
          onApply={async (fields: DictationFields) => {
            const payload = normalizeProposalDictation(fields);
            const res = await createFromDictationFn({ data: payload });
            setShowDictate(false);
            navigate({ to: "/admin/proposals/$id", params: { id: res.id } });
          }}
        />
      )}
    </div>
  );
}

function normalizeProposalDictation(f: DictationFields) {
  const items = Array.isArray(f.line_items) ? (f.line_items as Array<Record<string, unknown>>) : [];
  return {
    customer_name: String(f.customer_name ?? "").trim() || "Customer",
    customer_email: typeof f.customer_email === "string" && /.+@.+\..+/.test(f.customer_email) ? f.customer_email : null,
    customer_phone: typeof f.customer_phone === "string" ? f.customer_phone || null : null,
    property_address: typeof f.property_address === "string" ? f.property_address || null : null,
    project_type: typeof f.project_type === "string" ? f.project_type || null : null,
    estimated_timeline: typeof f.estimated_timeline === "string" ? f.estimated_timeline || null : null,
    scope: Array.isArray(f.scope) ? (f.scope as string[]).filter((s) => typeof s === "string" && s.trim()).slice(0, 40) : [],
    notes: typeof f.notes === "string" ? f.notes || null : null,
    line_items: items.map((it) => ({
      title: String(it.title ?? "Line item").slice(0, 200),
      description: typeof it.description === "string" ? it.description : null,
      area_or_quantity: it.area_or_quantity != null ? Number(it.area_or_quantity) || null : null,
      unit: typeof it.unit === "string" ? it.unit : null,
      unit_price: it.unit_price != null ? Number(it.unit_price) || null : null,
      total: Number(it.total ?? 0) || 0,
    })),
  };
}

function NewProposalDialog({ onClose, onCreate }: { onClose: () => void; onCreate: (v: any) => Promise<void> }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    property_address: "",
    project_type: "interior",
  });
  const mut = useMutation({
    mutationFn: async () => {
      const payload: any = { ...form };
      if (!payload.customer_email) delete payload.customer_email;
      if (!payload.customer_phone) delete payload.customer_phone;
      if (!payload.property_address) delete payload.property_address;
      await onCreate(payload);
    },
  });
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">New Proposal</p>
            <h2 className="font-serif text-xl text-white mt-1">Create blank proposal</h2>
            <p className="text-xs text-zinc-500 mt-1">For leads that skipped the AI estimate. You'll edit line items and pricing next.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="size-4" /></button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (form.customer_name.trim()) mut.mutate(); }}
          className="space-y-3"
        >
          <Field label="Customer name *" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} required />
          <Field label="Email" type="email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} />
          <Field label="Phone" value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} />
          <Field label="Property address" value={form.property_address} onChange={(v) => setForm({ ...form, property_address: v })} />
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Project type</label>
            <select
              value={form.project_type}
              onChange={(e) => setForm({ ...form, project_type: e.target.value })}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white"
            >
              <option value="interior">Interior</option>
              <option value="exterior">Exterior</option>
              <option value="cabinets">Cabinets</option>
              <option value="deck">Deck / Fence</option>
              <option value="commercial">Commercial</option>
              <option value="other">Other</option>
            </select>
          </div>
          {mut.error && <p className="text-xs text-red-400">{(mut.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-2 rounded-lg text-zinc-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={!form.customer_name.trim() || mut.isPending} className="text-xs px-4 py-2 rounded-lg bg-amber-300 text-zinc-950 font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              {mut.isPending && <Loader2 className="size-3.5 animate-spin" />} Create & edit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{label}</label>
      <input
        type={type} value={value} required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500"
      />
    </div>
  );
}